/**
 * AppShell — Standard application shell for all OpCentral apps.
 *
 * Wraps: SkinnrProvider → FramrProvider → PanelrProvider → Layout → children
 *
 * Usage:
 *   import { AppShell } from '@opcentral/framr-ui';
 *   import '@opcentral/skinnr-lib/theme/opcentral.css';
 *
 *   <AppShell namespace="publishr" appName="Publishr" layout="sidebar" navItems={items}>
 *     <Routes>...</Routes>
 *   </AppShell>
 *
 * Layout modes:
 *   - "minimal"  — TopBar + content (no sidebar)
 *   - "sidebar"  — TopBar + collapsible sidebar + content
 *   - "clean"    — Content only (no topbar, no sidebar)
 */

import React, { useState, useCallback } from 'react';
import { SkinnrProvider } from '@opcentral/skinnr-ui';
import { PanelrProvider } from '@opcentral/panelr-lib';
import { FramrProvider } from '../FramrContext.js';
import { AppTopBar } from './AppTopBar.js';
import { AppSideNav, type NavItem } from './AppSideNav.js';
import type { FrameConfig } from '../types.js';
import './AppShell.css';

export interface AppShellProps {
  /** Unique namespace for this app (used by providers for state isolation) */
  namespace: string;
  /** Display name shown in topbar */
  appName: string;
  /** Initial theme: 'light' | 'dark' */
  theme?: 'light' | 'dark';
  /** Layout mode */
  layout?: 'minimal' | 'sidebar' | 'clean';
  /** Navigation items for sidebar (only used in 'sidebar' layout) */
  navItems?: NavItem[];
  /** Custom topbar right-side content (e.g., user menu, notifications) */
  topBarActions?: React.ReactNode;
  /** App logo URL or emoji */
  logo?: string;
  /** Additional FrameConfig overrides */
  frameConfig?: Partial<FrameConfig>;
  /** Content */
  children: React.ReactNode;
}

export function AppShell({
  namespace,
  appName,
  theme = 'light',
  layout = 'sidebar',
  navItems = [],
  topBarActions,
  logo,
  frameConfig,
  children,
}: AppShellProps) {
  const [navCollapsed, setNavCollapsed] = useState(false);

  const toggleNav = useCallback(() => {
    setNavCollapsed((c) => !c);
  }, []);

  const framrConfig: FrameConfig = {
    appName,
    theme,
    layout: layout === 'clean' ? 'minimal' : 'default',
    showHeader: false, // We use AppTopBar instead of FrameHeader
    showFooter: false,
    showSidebar: false, // We use AppSideNav instead of FrameSidebar
    ...frameConfig,
  };

  return (
    <SkinnrProvider namespace={namespace} initialTheme={theme}>
      <FramrProvider initialConfig={framrConfig}>
        <PanelrProvider namespace={namespace}>
          <div className="oc-app-shell" data-layout={layout}>
            {/* TopBar — shown in 'minimal' and 'sidebar' layouts */}
            {layout !== 'clean' && (
              <AppTopBar
                appName={appName}
                logo={logo}
                showNavToggle={layout === 'sidebar'}
                onToggleNav={toggleNav}
                actions={topBarActions}
              />
            )}

            <div className="oc-app-body">
              {/* SideNav — only in 'sidebar' layout */}
              {layout === 'sidebar' && <AppSideNav items={navItems} collapsed={navCollapsed} />}

              {/* Main content */}
              <main className="oc-app-content">{children}</main>
            </div>
          </div>
        </PanelrProvider>
      </FramrProvider>
    </SkinnrProvider>
  );
}
